import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    Globe2,
    LayoutDashboard,
    Menu,
    Rocket,
    Sparkles,
    Users
} from "lucide-react";
import "./MobileLandingPage.css";

export default function MobileLandingPage() {
    const navigate = useNavigate();
    const [prompt, setPrompt] = useState("");

    const start = () => {
        const cleanPrompt = prompt.trim();
        const query = cleanPrompt
            ? `?prompt=${encodeURIComponent(cleanPrompt)}`
            : "";

        navigate(`/generate/mobile${query}`);
    };

    return (
        <main className="mlx-page">
            <header className="mlx-header">
                <div className="mlx-wordmark">Nexus</div>

                <button type="button" className="mlx-menu" aria-label="Open menu">
                    <Menu size={23} />
                </button>
            </header>

            <section className="mlx-hero">
                <div className="mlx-ai-badge">
                    <Sparkles size={14} />
                    <span>AI website generator</span>
                </div>

                <h1>Your website, CRM, and leads — built from one idea.</h1>

                <p>
                    Describe your business once. Shriansh Nexus prepares a website preview,
                    CRM modules, lead flow, and publish-ready workspace.
                </p>
            </section>

            <section className="mlx-prompt-card">
                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g. Build a real estate portal with property listings, enquiry leads and contact page..."
                />

                <button type="button" onClick={start}>
                    Start building
                    <ArrowRight size={17} />
                </button>
            </section>

            <section className="mlx-mini-proof">
                <span><CheckCircle2 size={14} />Website</span>
                <span><CheckCircle2 size={14} />CRM</span>
                <span><CheckCircle2 size={14} />Leads</span>
                <span><CheckCircle2 size={14} />Publish</span>
            </section>

            <section className="mlx-showcase">
                <div className="mlx-showcase-copy">
                    <span>Preview snapshot</span>
                    <strong>AI creates the first version instantly.</strong>
                </div>

                <div className="mlx-browser">
                    <div className="mlx-browser-top">
                        <span />
                        <span />
                        <span />
                    </div>

                    <div className="mlx-site-preview">
                        <div className="mlx-site-hero" />
                        <div className="mlx-site-row">
                            <span />
                            <span />
                            <span />
                        </div>
                        <div className="mlx-site-grid">
                            <span />
                            <span />
                            <span />
                            <span />
                        </div>
                    </div>
                </div>
            </section>

            <section className="mlx-benefits">
                <h2>Why Shriansh Nexus?</h2>

                <div className="mlx-benefit-card">
                    <Rocket size={22} />
                    <div>
                        <strong>Launch faster</strong>
                        <span>Generate a business-ready website flow without starting from blank pages.</span>
                    </div>
                </div>

                <div className="mlx-benefit-card blue">
                    <LayoutDashboard size={22} />
                    <div>
                        <strong>CRM is included</strong>
                        <span>Your leads, services, programs, listings, or products can be managed from CRM.</span>
                    </div>
                </div>

                <div className="mlx-benefit-card dark">
                    <Users size={22} />
                    <div>
                        <strong>Built for real businesses</strong>
                        <span>Designed for agencies, local companies, academies, real estate, commerce and services.</span>
                    </div>
                </div>
            </section>

            <section className="mlx-vision">
                <Globe2 size={26} />
                <h2>Our vision is simple.</h2>
                <p>
                    Make every business launch with a website, CRM, leads, and AI-powered content system from one workspace.
                </p>
            </section>

            <section className="mlx-faq">
                <h2>FAQ</h2>

                <details open>
                    <summary>
                        What does Nexus generate?
                        <ChevronDown size={18} />
                    </summary>
                    <p>
                        Nexus generates a website preview, content structure, CRM-ready modules,
                        lead forms, and a publish workflow.
                    </p>
                </details>

                <details>
                    <summary>
                        Can I edit after generation?
                        <ChevronDown size={18} />
                    </summary>
                    <p>
                        Yes. After saving, you can manage content, leads, modules, and publishing from the CRM dashboard.
                    </p>
                </details>

                <details>
                    <summary>
                        Is this only a website builder?
                        <ChevronDown size={18} />
                    </summary>
                    <p>
                        No. Shriansh Nexus is designed as a website + CRM builder, so generated sites are connected to business data.
                    </p>
                </details>
            </section>

            <footer className="mlx-footer">
                <strong>Shriansh Nexus</strong>
                <span>AI Website + CRM Builder</span>
                <button type="button" onClick={start}>
                    Start building
                    <ArrowRight size={16} />
                </button>
            </footer>
        </main>
    );
}